import $ from "jquery";
import _ from "underscore";
import Backbone from "backbone";
import stickyAlertMessageService from "src/shared/alert-messages/sticky-alert-message-service";
import ProgramDomainCollection from "src/shared/collections/ProgramDomainCollection";
import CampaignModel from "src/shared/campaigns/CampaignModel";
import featureFlagService from "src/shared/feature-flag-service";
import Strings from "src/shared/Strings";

const DEFAULT_LABEL = "default";
const OPTIONAL_PARAMETERS = "optional_child_labels";
const CAMPAIGN_ORDER = {
    LIVE: 0,
    PAUSED: 1,
    ENDED: 2,
    STOPPED: 3,
    NOT_LAUNCHED: 4,
};
const THEMES_ORDER = ["2-column", "influencer", "drop-a-hint"];
const LOCALES = {
    EN: "en",
    SP: "sp",
    FR: "fr",
};
const COLORS = {
    BLUE: "blue",
    GRAY: "gray",
    PINK: "pink",
    PURPLE: "purple",
};
const PROGRAM_TYPES = [
    {
        name: "refer-a-friend",
        display_name: "Refer a Friend",
        description:
            "Let your existing customers help you acquire new, higher quality customers through trusted referrals.",
        color: COLORS.PINK,
    },
    {
        name: "drop-a-hint",
        display_name: "Drop-a-Hint",
        description:
            'If Refer a Friend is "perfect for you", Drop a Hint is "perfect for me". Allow customers to ask for a specific product as a gift.',
        color: COLORS.PURPLE,
    },
    {
        name: "influencer",
        display_name: "Influencer",
        description:
            "Give your most influential advocates and influencers personalized links and unique rewards for acquiring new customers on your behalf.",
        color: COLORS.GRAY,
    },
    {
        name: "friends-and-family",
        display_name: "Friends & Family",
        description:
            "Announce special offers to important audiences and grant them the exclusive ability to share the offer with their friends.",
        color: COLORS.BLUE,
    },
    {
        name: "welcome-offer",
        display_name: "Welcome Offer",
        description:
            "Deliver a reward of your choosing when a new user lands on your site and signs up by entering their email address via popup.",
        color: COLORS.PINK,
    },
    {
        name: "nomination",
        display_name: "Nomination",
        description:
            "Connect more meaningfully with your community by rewarding and recognizing important people nominated by your customers.",
        color: COLORS.PURPLE,
    },
    {
        name: "sweepstakes",
        display_name: "Sweepstakes",
        description:
            "Excite your customers with a chance to win a sweepstakes!",
        color: COLORS.GRAY,
    },
    {
        name: "email-capture",
        display_name: "Email Capture",
        description:
            "Refer a friend to get opted-in emails to grow your email list size and market to.",
        color: COLORS.BLUE,
    },
    {
        name: "surprise-and-delight",
        display_name: "Surprise & Delight",
        description:
            "Build loyalty by offering rewards that are unexpected for activities that help drive awareness, engagement and new customer acquisition.",
    },
    {
        name: "challenge",
        display_name: "Challenge",
        description:
            "Drive action by challenging a group of customers to complete tasks in a certain timeframe in order to earn rewards.",
    },
];
function getDefaultProgramDomain(programDomains) {
    return (
        _.findWhere(programDomains, {
            redirect_program_id: null,
            is_extole_domain: false,
        }) ||
        _.findWhere(programDomains, {
            redirect_program_id: null,
        }) ||
        {}
    );
}

const findDefaultProgramDomainService = () => {
    const deferred = $.Deferred();
    const programDomainCollection = ProgramDomainCollection.create();

    $.when(programDomainCollection.fetch())
        .done((collection) => {
            const defaultDomainModel =
                getDefaultProgramDomain(collection).domain;
            if (defaultDomainModel) {
                deferred.resolve(defaultDomainModel);
            } else {
                deferred.reject();
            }
        })
        .fail(deferred.reject);

    return deferred;
};

function loadProgramTypeConfigurations() {
    const deferred = $.Deferred();
    deferred.resolve(PROGRAM_TYPES);
    return deferred;
}

// Locales should come from the campaign, this is future direction
function populateCampaignAttributes(campaignModel) {
    const locales = campaignModel.get("locales") || [];
    if (!campaignModel.has("locales")) {
        locales.push(LOCALES.EN);
    }
    campaignModel.set(
        "locales",
        _.map(locales, (locale) => locale.toLowerCase())
    );
    if (campaignModel.get("name") === "Drop-a-Hint") {
        campaignModel.set("program_type", "drop-a-hint");
    }
}

const programUpdateCount = {
    _count: 0,
    increment() {
        this._count++;
    },
    getValue() {
        return this._count;
    },
};

function isThemeAvailableToAll(theme) {
    const themesUnderFlag = {
        Sweepstakes: {
            isEnabled: featureFlagService.isOn(
                featureFlagService.FLAGS["Program Types"].SWEEPSTAKES
            ),
        },
    };

    return !themesUnderFlag[theme] || themesUnderFlag[theme].isEnabled;
}

const loadTemplateCampaigns = _.memoize(() => {
    const deferred = $.Deferred();
    let campaignTemplates = new CampaignTemplateCollection();
    $.when(campaignTemplates.fetch())
        .done(() => {
            campaignTemplates.sort().forEach(populateCampaignAttributes);
            const campaignTemplatesFiltered = campaignTemplates.filter(
                (campaign) => {
                    const themeName = campaign.get("name");
                    if (isThemeAvailableToAll(themeName)) {
                        return campaign;
                    }
                }
            );
            campaignTemplates = new CampaignTemplateCollection(
                campaignTemplatesFiltered
            );
            deferred.resolve(campaignTemplates);
        })
        .fail(deferred.reject);
    return deferred;
});

const loadCampaigns = _.memoize(
    () => {
        const deferred = $.Deferred();
        let campaigns = new CampaignCollection();
        $.when(campaigns.fetch())
            .done(() => {
                campaigns.sort().forEach(populateCampaignAttributes);
                const campaignsFiltered = campaigns.filter((campaign) => {
                    const themeName = campaign.get("theme_name");
                    if (isThemeAvailableToAll(themeName)) {
                        return campaign;
                    }
                });
                campaigns = new CampaignCollection(campaignsFiltered);
                deferred.resolve(campaigns);
            })
            .fail(deferred.reject);
        return deferred;
    },
    () => programUpdateCount.getValue()
);

function loadProgramAccessSettings() {
    const deferred = $.Deferred();
    let defaultSettings = [
        {
            type: "refer-a-friend",
            locked: false,
        },
        {
            type: "email-capture",
            locked: true,
        },
        {
            type: "drop-a-hint",
            locked: false,
        },
        {
            type: "influencer",
            locked: !featureFlagService.isOn(
                featureFlagService.FLAGS["Program Types"].INFLUENCER_PROGRAM
            ),
        },
        {
            type: "friends-and-family",
            locked: !featureFlagService.isOn(
                featureFlagService.FLAGS["Program Types"].FRIENDS_AND_FAMILY
            ),
        },
        {
            type: "welcome-offer",
            locked: false,
        },
        {
            type: "nomination",
            locked: !featureFlagService.isOn(
                featureFlagService.FLAGS["Program Types"].NOMINATION
            ),
        },
        {
            type: "sweepstakes",
            locked: !featureFlagService.isOn(
                featureFlagService.FLAGS["Program Types"].SWEEPSTAKES
            ),
        },
    ];

    if (
        featureFlagService.isOn(
            featureFlagService.FLAGS["Program Types"].ADVOCATE_MARKETING_PACKAGE
        )
    ) {
        defaultSettings = _.map(defaultSettings, (setting) => {
            if (
                _.contains(
                    [
                        "drop-a-hint",
                        "influencer",
                        "friends-and-family",
                        "welcome-offer",
                        "nomination",
                    ],
                    setting.type
                )
            ) {
                setting.locked = false;
            }
            return setting;
        });
    }

    const settings = [].concat(defaultSettings);
    deferred.resolve(settings);
    return deferred;
}

const loadProgramTypes = _.memoize(() => {
    const deferred = $.Deferred();

    $.when(
        loadProgramTypeConfigurations(),
        loadTemplateCampaigns(),
        loadCampaigns(),
        loadProgramAccessSettings()
    ).done(
        (
            programTypes,
            templateCampaigns,
            existingCampaigns,
            programAccessSettings
        ) => {
            function mapProgramTypes(programType) {
                const templateCampaigns =
                    templateCampaignsByProgramType[programType.name] || [];
                const existingCampaigns =
                    existingCampaignsByProgramType[programType.name] || [];

                const settings = _.findWhere(programAccessSettings, {
                    type: programType.name,
                });

                const locked = settings ? settings.locked : true;

                return {
                    name: programType.name,
                    display_name: programType.display_name,
                    description: programType.description,
                    image_url: programType.image_url,
                    color: programType.color,
                    template_campaigns: templateCampaigns,
                    existing_campaigns: existingCampaigns,
                    locked,
                };
            }

            const templateCampaignsByProgramType = {};
            templateCampaigns.forEach((templateCampaign) => {
                const programType = templateCampaign.get("program_type");
                templateCampaignsByProgramType[programType] =
                    templateCampaignsByProgramType[programType] || [];
                templateCampaignsByProgramType[programType].push(
                    templateCampaign.toJSON()
                );
            });

            const existingCampaignsByProgramType = {};
            existingCampaigns.forEach((existingCampaign) => {
                const programType = existingCampaign.get("program_type");
                existingCampaignsByProgramType[programType] =
                    existingCampaignsByProgramType[programType] || [];
                existingCampaignsByProgramType[programType].push(
                    existingCampaign.toJSON()
                );
            });
            const programs = _.chain(programTypes)
                .filter((programType) => {
                    if (isThemeAvailableToAll(programType.name)) {
                        return programType;
                    }
                })
                .map(mapProgramTypes)
                .value();
            deferred.resolve(new Backbone.Collection(programs));
        }
    );

    return deferred;
});

const CampaignCollection = Backbone.Collection.extend({
    model: CampaignModel,
    url: "/api/v2/campaign-summaries",
    comparator(campaign1, campaign2) {
        const order1 = campaign1.get("state");
        const order2 = campaign2.get("state");

        const difference = CAMPAIGN_ORDER[order1] - CAMPAIGN_ORDER[order2];
        if (difference === 0) {
            const name1 = campaign1.get("name");
            const name2 = campaign2.get("name");
            return name1.localeCompare(name2);
        }

        return difference;
    },
    parse(data) {
        return _.map(data, (campaign) =>
            _.extend(campaign, {
                stateFormatted: Strings.capitalize(campaign.state),
                stateClass: campaign.state.toLowerCase().replace("_", "-"),
            })
        );
    },
});

const CampaignTemplateCollection = Backbone.Collection.extend({
    model: CampaignModel,
    url: "/api/v2/campaigns/template",
    parse(themes) {
        return _.map(themes, (theme) =>
            _.extend(theme, {
                type: "TEMPLATE",
            })
        );
    },
    comparator(theme1, theme2) {
        const lowerCaseTheme1 = theme1.get("name").toLowerCase();
        const lowerCaseTheme2 = theme2.get("name").toLowerCase();

        const order1 = _.contains(THEMES_ORDER, lowerCaseTheme1)
            ? THEMES_ORDER.indexOf(lowerCaseTheme1)
            : Number.MAX_VALUE;
        const order2 = _.contains(THEMES_ORDER, lowerCaseTheme2)
            ? THEMES_ORDER.indexOf(lowerCaseTheme2)
            : Number.MAX_VALUE;

        return order1 - order2;
    },
});

const LabelInjectorModel = Backbone.Model.extend({
    idAttribute: "label_injector_id",

    urlRoot: "api:///v2/label-injectors",

    defaults: {
        name: null,
        forward_from_label: null,
        optional_child_labels: [],
    },

    sync(method, model, options) {
        const pickAttributes = [OPTIONAL_PARAMETERS];
        if (method === "create") {
            pickAttributes.push("name");
        }
        options.attrs = _.pick(this.attributes, pickAttributes);
        return Backbone.sync.call(this, method, model, options);
    },
    hasDefaultChildLabel() {
        const childLabels = this.get("optional_child_labels");
        return Boolean(_.contains(childLabels, DEFAULT_LABEL));
    },
});

const LabelInjectorCollection = Backbone.Collection.extend({
    url: "/api/v2/label-injectors",
    model: LabelInjectorModel,
});

const loadLabelInjectors = _.memoize(
    () => {
        const deferred = $.Deferred();
        const labelInjectors = new LabelInjectorCollection();
        $.when(labelInjectors.fetch())
            .done(() => {
                deferred.resolve(labelInjectors);
            })
            .fail(deferred.reject);
        return deferred;
    },
    () => programUpdateCount.getValue()
);

function changeProgramType(programLabel, value) {
    const data = { program_type: value };

    return $.ajax({
        type: "PUT",
        url: `api:///v2/campaigns/program/${programLabel}/update`,
        data: JSON.stringify(data),
        contentType: "application/json",
    });
}

export default {
    loadProgramTypes,
    loadTemplateCampaigns,
    loadCampaigns,
    loadLabelInjectors,
    findDefaultProgramDomainService,

    getLabelInjectorInformation(programLabel) {
        const deferred = $.Deferred();
        loadLabelInjectors(programLabel).done((labelInjectors) => {
            const labelInjector = labelInjectors.findWhere({
                name: programLabel,
            });
            let result = {};
            if (labelInjector) {
                result = _.extend(
                    {
                        isDefault: _.contains(
                            labelInjector.get("optional_child_labels"),
                            DEFAULT_LABEL
                        ),
                    },
                    labelInjector.toJSON()
                );
            }
            deferred.resolve(result);
        });
        return deferred;
    },

    getDefaultLabelInjectors() {
        const deferred = $.Deferred();
        loadLabelInjectors().done((labels) => {
            const defaultLabels = _.filter(labels.models, (label) =>
                _.contains(label.get("optional_child_labels"), DEFAULT_LABEL)
            );
            deferred.resolve(defaultLabels);
        });
        return deferred;
    },

    unsetAllDefaultLabels() {
        const deferred = $.Deferred();
        this.getDefaultLabelInjectors().done((labels) => {
            _.each(labels, (labelInjector) => {
                labelInjector.set(
                    "optional_child_labels",
                    _.without(
                        labelInjector.get("optional_child_labels"),
                        DEFAULT_LABEL
                    )
                );
                $.when(labelInjector.save())
                    .done(() => {
                        programUpdateCount.increment();
                        deferred.resolve();
                    })
                    .fail(deferred.reject);
            });
        });
        return deferred;
    },

    renameProgram(programLabel, newProgramLabel) {
        const deferred = $.Deferred();
        if (programLabel === newProgramLabel) {
            deferred.resolve();
        } else {
            $.ajax({
                url: `api:///v2/campaigns/program/${programLabel}/rename`,
                type: "POST",
                contentType: "application/json; charset=utf-8",
                data: JSON.stringify({
                    program_label: newProgramLabel,
                }),
            })
                .done(() => {
                    programUpdateCount.increment();
                    deferred.resolve();
                })
                .fail((response) => {
                    deferred.reject(response);
                });
        }
        return deferred;
    },

    ensureDefaultLabelInjectorExists(programLabel) {
        const deferred = $.Deferred();
        loadLabelInjectors(programLabel)
            .done((labelInjectors) => {
                const labelInjector = labelInjectors.findWhere({
                    name: programLabel,
                });
                if (!labelInjector) {
                    $.when(
                        labelInjectors.create({
                            name: programLabel,
                            optional_child_labels: [DEFAULT_LABEL],
                        })
                    )
                        .done(deferred.resolve)
                        .fail(deferred.reject);
                } else if (labelInjector.hasDefaultChildLabel()) {
                    deferred.resolve();
                } else {
                    labelInjector
                        .get("optional_child_labels")
                        .push(DEFAULT_LABEL);
                    $.when(labelInjector.save())
                        .done(deferred.resolve)
                        .fail(deferred.reject);
                }
            })
            .fail(deferred.reject);

        function resolve() {
            programUpdateCount.increment();
            deferred.resolve();
        }
        return deferred;
    },

    ensureNonDefaultLabelInjectorsExist(programLabel) {
        const deferred = $.Deferred();
        loadLabelInjectors(programLabel)
            .done((labelInjectors) => {
                const labelInjector = labelInjectors.findWhere({
                    name: programLabel,
                });
                if (!labelInjector) {
                    $.when(
                        labelInjectors.create({
                            name: programLabel,
                            optional_child_labels: [],
                        })
                    )
                        .done(deferred.resolve)
                        .fail(deferred.reject);
                } else if (
                    labelInjector &&
                    labelInjector.hasDefaultChildLabel()
                ) {
                    labelInjector.set(
                        "optional_child_labels",
                        _.without(
                            labelInjector.get("optional_child_labels"),
                            DEFAULT_LABEL
                        )
                    );
                    $.when(labelInjector.save())
                        .done(() => {
                            programUpdateCount.increment();
                            deferred.resolve();
                        })
                        .fail(deferred.reject);
                } else {
                    deferred.resolve();
                }
            })
            .fail(deferred.reject);
        return deferred;
    },

    isThemeAvailableToAll(theme) {
        return isThemeAvailableToAll(theme);
    },

    loadProgramTypeConfigurations() {
        return loadProgramTypeConfigurations();
    },

    changeProgramType(programLabel, value) {
        return changeProgramType(programLabel, value);
    },
};
